// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import approveOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/ApproveOrder";
import deleteOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/DeleteOrder";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useState } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: OrderApprovalDetailsActionButtonsStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);

    return {
        settingsCollection: getSettingsCollection(state),
        account: getCurrentAccountState(state)?.value,
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        orderApprovalListPageLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
        homeLink: getPageLinkByPageType(state, "HomePage"),
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
        locationId: session.pickUpWarehouse?.id,
        currency: session.currency?.currencyCode,
    };
};

const mapDispatchToProps = {
    approveOrder,
    deleteOrder,
    // XNG-Changes: add loading spinner on delete and approve order
    xCommonLoadingOverlay,
};

export interface OrderApprovalDetailsActionButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    printButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    approveButton?: ButtonPresentationProps;
    printClickable?: ClickableProps;
    continueShoppingClickable?: ClickableProps;
    deleteClickable?: ClickableProps;
    approveClickable?: ClickableProps;
    twoButtonModalStyles?: TwoButtonModalStyles;
}

export const orderApprovalDetailsActionButtonsStyles: OrderApprovalDetailsActionButtonsStyles = {
    deleteButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    continueShoppingButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    approveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        variant: "secondary",
        buttonType: "solid",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const OrderApprovalDetailsActionButtons = ({
    cart,
    history,
    cartPageLink,
    orderApprovalListPageLink,
    homeLink,
    approveOrder,
    deleteOrder,
    extendedStyles,
    account,
    toaster,
    // XNG-Changes
    xCommonLoadingOverlay,
    // xng-change: add google event
    xGoogleAnalytics,
    currentBillTo,
    locationId,
    currency,
}: Props) => {
    const [styles] = useState(() => mergeToNew(orderApprovalDetailsActionButtonsStyles, extendedStyles));
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [isDisabledApproveButton, setIsDisabledApproveButton] = useState(false);

    if (!cart || !account || !orderApprovalListPageLink || !cartPageLink) {
        return null;
    }

    const approvalDisabled = cart.cartLines?.every(cartline => cartline.isRestricted);

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const continueShoppingClickHandler = () => {
        if (homeLink) {
            history.push(homeLink.url);
        }
    };

    const deleteClickHandler = () => {
        // XNG-Changes
        xCommonLoadingOverlay({ key: "add", value: "delete_order_approval" });
        deleteOrder({
            onSuccess: () => {
                toaster.addToast({ body: translate("Order #{0} Deleted", cart.orderNumber), messageType: "success" });
                history.push(orderApprovalListPageLink.url);
                xCommonLoadingOverlay({ key: "clear", value: "delete_order_approval" });
            },
        });
    };

    const approveClickHandler = () => {
        setIsDisabledApproveButton(true);

        // XNG-Changes
        xCommonLoadingOverlay({ key: "add", value: "approve_order" });
        approveOrder({
            onSuccess: () => {
                history.push(cartPageLink.url);
                xCommonLoadingOverlay({ key: "clear", value: "approve_order" });
            },
            onComplete() {
                setTimeout(() => {
                    setIsDisabledApproveButton(false);
                }, 500);
            },
        });

        // xng-change: add google event
        if (xGoogleAnalytics.includeMyAccountOrderApproval) {
            const dataLayer = getDataLayer();

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.MY_ACCOUNT_ORDER_APPROVAL,
                ecommerce: {
                    transaction_id: cart?.orderNumber,
                    value: cart?.orderSubTotal,
                    tax: cart?.totalTax,
                    shipping: cart?.shippingAndHandling,
                    currency,
                    items: cart?.cartLines?.map(cartLine => ({
                        item_id: cartLine.erpNumber,
                        item_name: cartLine.shortDescription,
                        affiliation: currentBillTo?.companyName,
                        index: cartLine?.line,
                        item_brand: cartLine.brand?.name,
                        location_id: locationId,
                        price: cartLine.pricing?.unitNetPrice,
                        quantity: cartLine.qtyOrdered,
                        ...parseProductCategories(cartLine.properties?.categories),
                    })),
                    brand_code: currentBillTo?.properties?.brand,
                    exclusive: currentBillTo?.properties?.exclusive,
                    group_descr: currentBillTo?.properties?.groupDescription,
                },
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    const printLabel = translate("Print");
    const deleteLabel = translate("Delete Order");
    const approveLabel = translate("Approve / Edit Order button");
    const continueShoppingLabel = translate("Continue Shopping");

    return (
        <>
            <Hidden {...styles.menuHiddenContainer}>
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <Clickable {...styles.printClickable} onClick={printOrOpenPrintAllModal} aria-label={printLabel}>
                        {printLabel}
                    </Clickable>
                    {account.canApproveOrders ? (
                        <>
                            <Clickable
                                {...styles.deleteClickable}
                                onClick={() => setConfirmationModalIsOpen(true)}
                                aria-label={deleteLabel}
                            >
                                {deleteLabel}
                            </Clickable>
                            <Clickable
                                disabled={approvalDisabled || isDisabledApproveButton}
                                {...styles.approveClickable}
                                onClick={approveClickHandler}
                                aria-label={approveLabel}
                            >
                                {approveLabel}
                            </Clickable>
                        </>
                    ) : (
                        <Clickable
                            {...styles.continueShoppingClickable}
                            onClick={continueShoppingClickHandler}
                            aria-label={continueShoppingLabel}
                        >
                            {continueShoppingLabel}
                        </Clickable>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.buttonsHiddenContainer}>
                <Button
                    data-test-selector="orderApprovalDetails_print"
                    {...styles.printButton}
                    onClick={printOrOpenPrintAllModal}
                    aria-label={printLabel}
                >
                    {printLabel}
                </Button>
                {account.canApproveOrders ? (
                    <>
                        <Button
                            data-test-selector="orderApprovalDetails_deleteOrder"
                            {...styles.deleteButton}
                            onClick={() => setConfirmationModalIsOpen(true)}
                            aria-label={deleteLabel}
                        >
                            {deleteLabel}
                        </Button>
                        <Button
                            data-test-selector="orderApprovalDetails_approveOrder"
                            {...styles.approveButton}
                            disabled={approvalDisabled || isDisabledApproveButton}
                            onClick={approveClickHandler}
                            aria-label={approveLabel}
                        >
                            {approveLabel}
                        </Button>
                    </>
                ) : (
                    <Button
                        data-test-selector="orderApprovalDetails_continueShopping"
                        {...styles.continueShoppingButton}
                        onClick={continueShoppingClickHandler}
                        aria-label={continueShoppingLabel}
                    >
                        {continueShoppingLabel}
                    </Button>
                )}
            </Hidden>
            <TwoButtonModal
                modalIsOpen={confirmationModalIsOpen}
                headlineText={translate("Delete Order")}
                messageText={translate("Do you want to delete order #{0}?", cart.orderNumber)}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                submitTestSelector="confirmDeleteOrderButton"
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={() => setConfirmationModalIsOpen(false)}
                onSubmit={deleteClickHandler}
            ></TwoButtonModal>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withToaster(OrderApprovalDetailsActionButtons)));
